import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useCartPricingRefreshing } from '../contexts/CartPricingContext';
import { useLocale } from '../contexts/LocaleContext';
import { priceCartForPreview } from '../lib/pricing';
import * as types from '../types';
import useHardwareFirstCheckout from './useHardwareFirstCheckout';

const UPSELL_PREVIEW_CART_ITEM_ID = 'hardware-first-upsell-preview';

/**
 * Server-resolved price for adding one more unit of `sku` to the store cart. Prices the candidate
 * alongside the cart's existing items, so the tile quotes the additional-tracker rate rather than
 * the first-unit rate `useHardwareFirstPdpPricing` returns. Undefined while loading, when not
 * enrolled, or when the pricing call fails.
 */
export default function useHardwareFirstUpsellPricing(
  sku: string | undefined,
): types.HardwareDiscountPreview | undefined {
  const enrolled = useHardwareFirstCheckout();
  const { currency } = useLocale();
  const storeCart = useSelector((state: types.AppState) => state.storeShop.cart);
  // previews with the cart's coupon queue behind each other, so the tiles wait for the cart's own total to land
  const cartPricingRefreshing = useCartPricingRefreshing();
  const [discount, setDiscount] = useState<types.HardwareDiscountPreview | undefined>(undefined);

  useEffect(() => {
    if (!enrolled || !sku) {
      setDiscount(undefined);
      return undefined;
    }

    if (cartPricingRefreshing) {
      return undefined;
    }

    let cancelled = false;
    const cart: types.StoreCart = {
      ...storeCart,
      cartItems: {
        ...storeCart.cartItems,
        [UPSELL_PREVIEW_CART_ITEM_ID]: {
          cartItemId: UPSELL_PREVIEW_CART_ITEM_ID,
          lineItem: { lineItemId: UPSELL_PREVIEW_CART_ITEM_ID, sku },
          quantity: 1,
        },
      },
      currency,
    };

    priceCartForPreview(cart)
      .then((pricing) => {
        if (!cancelled) {
          setDiscount(pricing.hardwareDiscounts?.[UPSELL_PREVIEW_CART_ITEM_ID]);
        }
      })
      // already reported inside priceCartForPreview; the tile then omits its price
      .catch(() => {
        if (!cancelled) {
          setDiscount(undefined);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enrolled, sku, currency, storeCart, cartPricingRefreshing]);

  return discount;
}
